import { useEffect, useRef, useState } from "react";
import {
  IconSync,
  IconDownload,
  IconMail,
  IconCheckSquare,
  IconTracking,
  IconCaretDown,
  IconSend,
} from "./icons";
import { prettifyApiError } from "@/shared/api";
import type { Order } from "../types";
import {
  downloadAllServiceTracking,
  generateAllServiceLdvBulk,
  ordersWithAllServiceSku,
} from "../allserviceApi";
import { ordersCosma } from "../cosmaApi";
import { useOrdinaFornitore } from "../useOrdinaFornitore";
import { POLEEPO_BULK_STATUSES, generateLabelsBulk, preflightLabels } from "../api";
import { STATO_SPEDITO } from "../confermaBulk";
import { CarrierSwitchConfirmDialog } from "./CarrierSwitchConfirmDialog";

interface SplitMenuItem {
  id: SyncStep;
  label: string;
  desc: string;
  icon: React.ReactNode;
  bgClass: string;
  textClass: string;
}

export type SyncStep = "orders" | "mail";

const SPLIT_ITEMS: SplitMenuItem[] = [
  {
    id: "orders",
    label: "Scarica ordini",
    desc: "Importa nuovi ordini dai marketplace",
    icon: (
      <IconDownload className="w-[14px] h-[14px] fill-none stroke-current stroke-[1.8] [stroke-linecap:round] [stroke-linejoin:round]" />
    ),
    bgClass: "bg-[rgba(79,107,138,0.14)]",
    textClass: "text-blue",
  },
  {
    // Audit 2026-05-28 (Davide): label rinominata da "Processa Mail" a
    // "Processa Mail Fornitori" per chiarire che NON sincronizza i
    // thread marketplace (quelli stanno su MessaggiPage / pulsante
    // "Sincronizza messaggi") ma legge le conferme spedizione IMAP
    // dal modulo supplier_mail.
    id: "mail",
    label: "Processa Mail Fornitori",
    desc: "Legge conferme spedizione fornitori da IMAP Hostinger",
    icon: (
      <IconMail className="w-[14px] h-[14px] fill-none stroke-current stroke-[1.8] [stroke-linecap:round] [stroke-linejoin:round]" />
    ),
    bgClass: "bg-[rgba(79,107,138,0.14)]",
    textClass: "text-blue",
  },
];

interface OrdiniActionToolbarProps {
  disabled?: boolean;
  busyStep: SyncStep | "all" | null;
  onSyncAll: () => void;
  onSyncStep: (step: SyncStep) => void;
  selectedOrders?: Order[];
  onAllServiceMessage?: (message: string) => void;
  onAllServiceError?: (message: string) => void;
  onAfterAllServiceAction?: () => void;
  /** Bulk change-status #11 legacy parity: handler che riceve i ref
   *  selezionati + lo stato Poleepo scelto dal dropdown. */
  onBulkChangeStatus?: (orderRefs: string[], newStatus: string) => Promise<void>;
}

// Tasti pieni (Davide 2026-10-28: «pieni come Sincronizza»; prima erano a
// contorno): da spenti solo sbiaditi, accesi un filo piu' scuri/chiari al passaggio.
const fillOff = "opacity-45 cursor-not-allowed";
const fillHover = "hover:brightness-95 dark:hover:brightness-110";

// Metrica condivisa dei tasti azione. Era `px-[14px] py-2 text-[13px]` copiata
// su sei bottoni: Davide (2026-07-27) li vuole piu' piccoli, e con sei copie
// "piu' piccoli" sono sei modifiche che divergono alla prima dimenticanza.
// Il raggio di bordo NO: i due split-button arrotondano solo un lato.
//
// Altezza ESPLICITA e non derivata dal padding: il gruppo stato contiene una
// <select> nativa, che si dimensiona sul proprio line-height interno e non
// sul nostro. Con `py-*` restava 1,75px piu' bassa dei tasti accanto — un
// gradino visibile su una riga allineata al centro. Un numero solo, condiviso.
// 30px (era 34) come ricerca, date e tendine della plancia unica (2026-09-23):
// due altezze diverse in una scheda sola si leggono come due schede.
// Telefono (2026-10-23): 48px, un tasto per riga a tutta larghezza.
const ACTION_H = "h-[34px] max-md:h-12";
const actionBtn = `inline-flex items-center gap-1.5 ${ACTION_H} px-3.5 text-[12.5px] font-semibold transition-all duration-[120ms] max-md:grow max-md:justify-center max-md:gap-2 max-md:px-4 max-md:text-[15px] max-md:font-semibold`;
// Caret degli split-button: stessa altezza del corpo, largo la meta'.
const actionCaret = `inline-flex items-center ${ACTION_H} px-2 transition-all duration-[120ms] max-md:px-4`;

function MiniSpinner({ className = "w-[14px] h-[14px]" }: { className?: string }) {
  return (
    <svg className={`${className} animate-spin`} viewBox="0 0 24 24" aria-hidden="true">
      <circle
        cx="12"
        cy="12"
        r="9"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeOpacity="0.25"
      />
      <path
        d="M21 12a9 9 0 0 0-9-9"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

export function OrdiniActionToolbar({
  disabled = false,
  busyStep,
  onSyncAll,
  onSyncStep,
  selectedOrders = [],
  onAllServiceMessage,
  onAllServiceError,
  onAfterAllServiceAction,
  onBulkChangeStatus,
}: OrdiniActionToolbarProps) {
  const [splitOpen, setSplitOpen] = useState(false);
  // Tracking AS: job lungo sull'unico AllServiceAdapter, gatato insieme a
  // tutto il resto (audit 2026-05-11 H6).
  const [trackingBusy, setTrackingBusy] = useState(false);
  // «Ordina al fornitore»: flussi, anteprime e popup vivono nell'hook, che
  // condividiamo con la pagina ordine (Davide 2026-10-23).
  const ordina = useOrdinaFornitore({
    onMessage: (m) => onAllServiceMessage?.(m),
    onError: (m) => onAllServiceError?.(m),
    onRefresh: () => onAfterAllServiceAction?.(),
  });
  // Bottone unificato "Genera LDV": copre sia il bulk xlsx AllService che
  // il bulk PDF Poleepo (audit 2026-05-28 Davide: due bottoni con stessa
  // label confondevano). Click → smista la selezione in AS + non-AS e
  // lancia i due endpoint in parallelo via Promise.allSettled, riportando
  // un esito combinato. Un solo flag busy semplifica il gating.
  const [unifiedLdvBusy, setUnifiedLdvBusy] = useState(false);
  // Switch SDA→Poste Delivery oltre 15 colli (spec 2026-06-24): ordini del
  // gruppo Poleepo che il preflight segnala sopra soglia. ``null`` = nessun
  // dialog. Mentre il dialog è aperto ``unifiedLdvBusy`` resta true (gate degli
  // altri bottoni). Mai mutare in-place: ogni apertura/chiusura via setState.
  const [carrierSwitchItems, setCarrierSwitchItems] = useState<Array<{
    reference: string;
    packages_count: number;
  }> | null>(null);
  // Dropdown del tasto unico "Ordina al fornitore" (Davide 2026-08-13): elenca
  // i fornitori PRESENTI nella selezione, piu' l'opzione Cosma "Invio forzato"
  // che bypassa la dedup sent-log (force=true) per ri-inviare ordini gia'
  // caricati. ``force`` e' un PARAMETRO di ``runCosma``, non uno stato globale:
  // la voce di menu sceglie normale vs forzato al momento del click.
  const [supplierMenuOpen, setSupplierMenuOpen] = useState(false);
  // Default «Spedito» (Davide 2026-08-21): questo comando e' il modo con cui
  // si spedisce a lotti, e partire da un altro stato costringeva a cambiarlo
  // ogni volta — cioe' a ricordarselo ogni volta.
  const [statusToSet, setStatusToSet] = useState<string>(STATO_SPEDITO);
  const [bulkStatusBusy, setBulkStatusBusy] = useState(false);
  const splitRef = useRef<HTMLDivElement>(null);
  const supplierSplitRef = useRef<HTMLDivElement>(null);
  const isBusy = busyStep !== null;

  // Stato ordine: spento senza selezione o mentre gira un'altra azione. Il
  // `title` sul gruppo dice PERCHE' e' spento — un controllo grigio e muto
  // manda l'operatore a cliccare a vuoto (regola: ogni tasto da' feedback).
  const bulkStatusDisabled = disabled || isBusy || bulkStatusBusy || selectedOrders.length === 0;
  const bulkStatusTitle =
    selectedOrders.length === 0
      ? "Seleziona almeno un ordine per cambiarne lo stato su Poleepo"
      : bulkStatusBusy
        ? "Aggiornamento stato in corso…"
        : `Applica il nuovo stato Poleepo a ${selectedOrders.length} ordin${
            selectedOrders.length === 1 ? "e" : "i"
          } selezionat${selectedOrders.length === 1 ? "o" : "i"}`;

  // Bulk-aware: filtriamo gli ordini AS dalla selezione corrente. Davide
  // chiede esplicitamente di poter agire su N ordini insieme — replica
  // _on_ldv_diretti / _on_mail_as_batch del legacy tab_ordini.py.
  const allServiceSelected = ordersWithAllServiceSku(selectedOrders);
  // Ordini selezionati NON-AllService: candidati al bulk "Genera LDV"
  // generico (Poleepo). AllService usa il bulk xlsx dedicato.
  const allServiceSelectedIds = new Set(allServiceSelected.map((o) => o.id));
  const nonAsSelected = selectedOrders.filter((o) => !allServiceSelectedIds.has(o.id));
  // Cosma nella selezione: serve solo alla voce «invio forzato» del menu.
  const cosmaSelected = ordersCosma(selectedOrders);

  // ponytail: gate comune a TUTTI i tasti della toolbar — un'azione in volo o un
  // dialog aperto blocca ogni altro flusso.
  //
  // Perche' e' unico e non per-bottone: i flussi condividono le stesse risorse.
  // Tracking AS e mail AS usano l'unico AllServiceAdapter (audit 2026-05-11 H6:
  // due job lunghi concorrenti → race su xlsx output dir); un popup Cosma aperto
  // deve fermare anche le azioni AS (con la tendina fornitore si passa
  // AS→COSMA→AS: senza il gate un'azione AS partirebbe durante l'upload FTP).
  // Ogni bottone aggiunge SOLO la propria condizione di selezione.
  const anyActionBusy =
    disabled || isBusy || trackingBusy || ordina.busy || ordina.dialogBusy || unifiedLdvBusy;

  // Bottone unificato: attivo se la selezione contiene ALMENO un ordine
  // (AS o non-AS). Lo smistamento avviene in runUnifiedLdv.
  const unifiedLdvDisabled = anyActionBusy || selectedOrders.length === 0;
  const trackingDisabled = anyActionBusy;

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (splitRef.current && !splitRef.current.contains(e.target as Node)) {
        setSplitOpen(false);
      }
      // Click fuori dal menu fornitori → chiudi. Ref dedicato: i due dropdown
      // sono indipendenti (chiudere l'uno non deve toccare l'altro).
      if (supplierSplitRef.current && !supplierSplitRef.current.contains(e.target as Node)) {
        setSupplierMenuOpen(false);
      }
    };
    // Esc chiude entrambi i dropdown (accessibilità tastiera).
    const keyHandler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setSplitOpen(false);
        setSupplierMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    document.addEventListener("keydown", keyHandler);
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("keydown", keyHandler);
    };
  }, []);

  // ponytail: mailAsTooltip/wfTooltip/cosmaTooltip/ithTooltip rimossi — erano
  // i tooltip dei cinque tasti per-fornitore, sostituiti dal solo
  // ``supplierOrderTooltip`` del tasto unico (Davide 2026-08-13).

  // Tooltip dinamico del bottone unificato "Genera LDV": mostra sempre
  // la ripartizione AS/Poleepo della selezione corrente. Serve a Davide
  // per capire a colpo d'occhio cosa partira' al click + perche' il
  // bottone e' eventualmente disabilitato. Regola "ogni pulsante = sempre
  // feedback".
  function unifiedLdvTooltip(): string {
    if (selectedOrders.length === 0) return "Seleziona almeno un ordine";
    if (unifiedLdvBusy) return "Generazione LDV in corso…";
    const a = allServiceSelected.length;
    const p = nonAsSelected.length;
    const parts: string[] = [];
    if (a > 0) parts.push(`${a} AllService (xlsx)`);
    if (p > 0) parts.push(`${p} Poleepo (PDF)`);
    return `Genera LDV per ${parts.join(" + ")}`;
  }

  /**
   * Bottone unificato "Genera LDV" (audit 2026-05-28). Smista la
   * selezione in due gruppi mutualmente esclusivi:
   *   - AllService → bulk xlsx via ``generateAllServiceLdvBulk``
   *   - non-AS (Poleepo) → bulk PDF via ``generateLabelsBulk``
   *
   * SDA oltre 15 colli (spec 2026-06-24): PRIMA di generare facciamo un
   * ``preflightLabels`` sul SOLO gruppo Poleepo. Se ci sono ordini sopra soglia
   * apriamo il dialog cumulativo e aspettiamo la scelta; altrimenti procediamo
   * subito. La generazione vera vive in ``executeUnifiedLdv``.
   */
  async function runUnifiedLdv() {
    if (unifiedLdvDisabled) return;
    setUnifiedLdvBusy(true);
    onAllServiceMessage?.("");
    onAllServiceError?.("");
    // Preflight SOLO sul gruppo Poleepo (read-only, nessuna spedizione): SDA con
    // >15 colli → propone Poste Delivery Business. AllService non ha questo
    // concetto (bulk xlsx) e non passa di qui.
    if (nonAsSelected.length > 0) {
      try {
        const items = await preflightLabels(nonAsSelected.map((o) => o.id));
        const over = items.filter((it) => it.needs_switch);
        if (over.length > 0) {
          // Busy resta true: il dialog aperto gata gli altri bottoni finché
          // l'utente non sceglie. Poi lo rilascia ``executeUnifiedLdv`` (nel
          // suo finally) sulla conferma, ``cancelCarrierSwitch`` sull'annulla.
          setCarrierSwitchItems(
            over.map((it) => ({ reference: it.reference, packages_count: it.packages_count })),
          );
          return;
        }
      } catch {
        // Preflight non determinante (rete/contratto): NON blocchiamo l'azione.
        // Il backend resta la guardia autoritativa — col bulk senza consenso
        // gli ordini sopra soglia finiranno tra i "saltati" del riepilogo.
      }
    }
    await executeUnifiedLdv(false);
  }

  /**
   * Esegue la generazione bulk (AS + Poleepo in parallelo via
   * ``Promise.allSettled``: la failure di un gruppo non blocca l'altro).
   * ``allowCarrierSwitch`` propaga al bulk Poleepo il consenso allo switch
   * SDA→Poste Delivery per gli ordini oltre 15 colli. Esito: stringa unica con i
   * conteggi + eventuali saltati + primo errore (regola "sempre feedback").
   */
  async function executeUnifiedLdv(allowCarrierSwitch: boolean) {
    onAllServiceMessage?.("");
    onAllServiceError?.("");

    type GroupOutcome = {
      kind: "as" | "poleepo";
      ok: number;
      err: number;
      skipped: number;
      firstError: string | null;
      fileNames: string[];
    };

    const tasks: Promise<GroupOutcome>[] = [];

    if (allServiceSelected.length > 0) {
      tasks.push(
        generateAllServiceLdvBulk(allServiceSelected.map((o) => o.id)).then(
          (r): GroupOutcome => ({
            kind: "as",
            ok: r.generated.length,
            err: r.errors.length,
            skipped: 0,
            firstError: r.errors[0]?.message ?? null,
            fileNames: r.generated.map((g) => g.file_name),
          }),
        ),
      );
    }

    if (nonAsSelected.length > 0) {
      tasks.push(
        generateLabelsBulk(
          nonAsSelected.map((o) => o.id),
          // ``{}`` quando non si consente lo switch: ``allow_carrier_switch``
          // resta undefined e ``JSON.stringify`` lo omette (body retrocompatibile).
          allowCarrierSwitch ? { allow_carrier_switch: true } : {},
        ).then(
          (r): GroupOutcome => ({
            kind: "poleepo",
            ok: r.generated.length,
            err: r.errors.length,
            skipped: r.needs_switch?.length ?? 0,
            firstError: r.errors[0] ? `${r.errors[0].reference}: ${r.errors[0].message}` : null,
            fileNames: [],
          }),
        ),
      );
    }

    try {
      const settled = await Promise.allSettled(tasks);
      let totOk = 0;
      let totErr = 0;
      let totSkipped = 0;
      const okParts: string[] = [];
      const errMessages: string[] = [];
      for (const r of settled) {
        if (r.status === "fulfilled") {
          totOk += r.value.ok;
          totErr += r.value.err;
          totSkipped += r.value.skipped;
          const label = r.value.kind === "as" ? "AS" : "Poleepo";
          if (r.value.ok > 0) okParts.push(`${r.value.ok} ${label}`);
          if (r.value.firstError) errMessages.push(r.value.firstError);
        } else {
          totErr += 1;
          errMessages.push(prettifyApiError(r.reason));
        }
      }
      const head =
        totOk === 0
          ? `LDV: 0 generate`
          : `LDV: ${totOk} generat${totOk === 1 ? "a" : "e"} (${okParts.join(" + ")})`;
      const skippedPart =
        totSkipped > 0
          ? ` · ${totSkipped} saltat${totSkipped === 1 ? "o" : "i"} (oltre 15 colli SDA, switch a Poste Delivery non confermato)`
          : "";
      const tail =
        totErr > 0
          ? `, ${totErr} error${totErr === 1 ? "e" : "i"}${errMessages[0] ? ` — ${errMessages[0]}` : ""}`
          : "";
      const msg = `${head}${skippedPart}${tail}.`;
      if (totOk === 0 && totErr > 0) onAllServiceError?.(msg);
      else onAllServiceMessage?.(msg);
      onAfterAllServiceAction?.();
    } finally {
      setUnifiedLdvBusy(false);
    }
  }

  // Conferma dialog bulk: procede con lo switch (allow_carrier_switch=true).
  function confirmCarrierSwitch() {
    setCarrierSwitchItems(null);
    void executeUnifiedLdv(true);
  }

  // Annulla / Esc / click fuori: il lotto si FERMA, nessuna LDV viene generata
  // (nemmeno per gli ordini sotto soglia, nemmeno il gruppo AllService).
  //
  // Perche' non "genero il resto e salto gli over-soglia": una LDV e' denaro
  // vero (etichetta di spedizione, non annullabile con un clic) e "Annulla" e'
  // il tasto con cui l'operatore dice NO. Esc e il click sull'overlay passano
  // dallo stesso handler: un tasto premuto per sbaglio mandava in stampa
  // l'intera selezione. E' anche la semantica gia' vera sul SINGOLO ordine
  // (``useLabelActions.cancelCarrierSwitch``): li' l'Annulla non genera nulla.
  // Chi vuole le altre LDV deseleziona gli ordini sopra soglia e rilancia.
  //
  // Il busy va spento QUI: senza ``executeUnifiedLdv`` non c'e' piu' il suo
  // ``finally`` a rilasciarlo, e la toolbar resterebbe bloccata per sempre.
  function cancelCarrierSwitch() {
    const sopraSoglia = carrierSwitchItems?.length ?? 0;
    setCarrierSwitchItems(null);
    setUnifiedLdvBusy(false);
    onAllServiceMessage?.(
      `Generazione LDV annullata: nessuna etichetta creata. ` +
        `${sopraSoglia} ordin${sopraSoglia === 1 ? "e" : "i"} oltre i 15 colli SDA — ` +
        `deselezional${sopraSoglia === 1 ? "o" : "i"} per generare le altre LDV.`,
    );
  }

  /**
   * Workflow one-click "Tracking AS" (2026-05-11):
   *   1. Scarica file generale dall'area riservata AllService (timeout 600s)
   *   2. Parsa xlsx, importa tracking in DB
   *   3. Per ogni tracking nuovo, crea uno shipping su Poleepo che propaga
   *      il codice al marketplace via webhook
   *   4. Mostra un'unica pill di esito (no piu' dialog/popup intermedi).
   * Davide chiede esplicitamente: "fai in modo che quel tasto prenda i
   * tracking e li mandi a Poleepo senza popup".
   */
  async function runTracking() {
    if (trackingDisabled) return;
    setTrackingBusy(true);
    onAllServiceMessage?.("");
    onAllServiceError?.("");
    try {
      const result = await downloadAllServiceTracking();
      const nuovi = result.poleepo_pushed;
      const base =
        nuovi === 1
          ? "Tracking AS: 1 nuovo tracking caricato su Poleepo"
          : `Tracking AS: ${nuovi} nuovi tracking caricati su Poleepo`;
      if (result.poleepo_errors > 0) {
        // Errori VERI residui (non i "gia' spedito", ora classificati skip):
        // non li nascondiamo.
        onAllServiceError?.(`${base} — ${result.poleepo_errors} con errore, controlla i log`);
      } else {
        onAllServiceMessage?.(base);
      }
      onAfterAllServiceAction?.();
    } catch (err) {
      onAllServiceError?.(prettifyApiError(err));
    } finally {
      setTrackingBusy(false);
    }
  }

  // ── Tasto UNICO "Ordina al fornitore" (Davide 2026-08-13) ────────────────
  //
  // Erano cinque tasti affiancati — AS, WF, Cosma, Ityhome, Itamoby — accesi o
  // spenti a seconda di cosa avevi selezionato: la toolbar chiedeva a Davide di
  // sapere PRIMA di che fornitore fosse l'ordine, per scegliere il tasto giusto.
  // Ora la selezione lo dice da sola: un tasto, e il flusso lo smista lei.
  //
  // Ogni flusso resta INTATTO (stesso preview, stesso popup, stesso invio): qui
  // cambia solo chi lo lancia. E resta intatta anche la regola anti-errore piu'
  // importante — ogni flusso agisce SOLO sul sottoinsieme del SUO fornitore, mai
  // sull'intera selezione: un ordine Itamoby non puo' finire a Cosma nemmeno
  // selezionandoli insieme.
  const supplierFlows = ordina.flussi(selectedOrders);

  // Selezione di UN solo fornitore: il caso normale (Davide filtra per
  // fornitore prima di selezionare). ``null`` = zero o piu' d'uno.
  const soloFlow = supplierFlows.length === 1 ? (supplierFlows[0] ?? null) : null;

  // Spento quando NESSUN ordine selezionato ha un fornitore con flusso d'invio.
  const supplierOrderDisabled = anyActionBusy || supplierFlows.length === 0;
  // Un flusso in volo qualsiasi: il tasto mostra lo spinner (prima ogni bottone
  // aveva il suo, ora e' uno solo e deve reagire a tutti).
  const supplierOrderBusy = ordina.busy;

  function supplierOrderTooltip(): string {
    if (supplierOrderBusy) return "Preparazione dell'ordine al fornitore in corso…";
    if (selectedOrders.length === 0) return "Seleziona almeno un ordine da ordinare al fornitore";
    if (supplierFlows.length === 0)
      return "Nessuno degli ordini selezionati ha un fornitore a cui inviare l'ordine (AllService, Web Furniture, Aurora Bedding, Mauro Ferretti, Cosma, Ityhome, Itamoby, Bizzotto)";
    const riepilogo = supplierFlows.map((f) => `${f.count} ${f.label}`).join(" · ");
    return soloFlow
      ? `Ordina a ${soloFlow.label}: ${riepilogo}`
      : `Fornitori diversi nella selezione (${riepilogo}): scegli da quale partire`;
  }

  // Click sul corpo: un solo fornitore → parte diretto (il caso normale, zero
  // click in piu' di prima); piu' fornitori → apre la tendina invece di
  // decidere al posto di Davide quale ordine parte per primo.
  function onSupplierOrderClick() {
    if (supplierOrderDisabled) return;
    if (soloFlow) {
      setSupplierMenuOpen(false);
      soloFlow.run();
      return;
    }
    setSupplierMenuOpen((prev) => !prev);
  }

  const supplierOrderLabel = supplierOrderBusy
    ? "Preparo…"
    : soloFlow
      ? `Ordina a ${soloFlow.label}${soloFlow.count > 1 ? ` (${soloFlow.count})` : ""}`
      : "Ordina al fornitore";

  return (
    // Nessuna cornice propria: la toolbar e' la riga azioni della plancia unica
    // (OrdiniPage, Davide 2026-09-23), sotto i filtri e accanto ai ritiri.
    <div>
      <div className="flex flex-wrap items-center gap-2 max-md:flex-col max-md:items-stretch max-md:gap-3">
        {/* Split button — Sincronizza */}
        <div
          ref={splitRef}
          className="relative inline-flex items-stretch max-md:flex max-md:flex-wrap"
          data-testid="split-btn"
        >
          <button
            type="button"
            className={`${actionBtn} rounded-l-full bg-violet text-white border border-violet hover:bg-violet-2 hover:border-violet-2 disabled:opacity-55 disabled:cursor-not-allowed`}
            disabled={disabled || isBusy}
            aria-busy={isBusy}
            onClick={() => {
              setSplitOpen(false);
              onSyncAll();
            }}
          >
            {isBusy || trackingBusy ? (
              <MiniSpinner />
            ) : (
              <IconSync className="w-[14px] h-[14px] fill-none stroke-white stroke-[1.6] [stroke-linecap:round] [stroke-linejoin:round]" />
            )}
            {/* Tracking AS: parte dal menu, che si chiude; il feedback resta qui. */}
            {trackingBusy
              ? "Tracking AS…"
              : busyStep === "all"
                ? "Sincronizzo..."
                : busyStep === "orders"
                  ? "Ordini..."
                  : busyStep === "mail"
                    ? "Mail fornitori..."
                    : "Sincronizza"}
          </button>
          <button
            type="button"
            className={`${actionCaret} rounded-r-full bg-violet text-white border border-violet border-l-white/25 hover:bg-violet-2 hover:border-violet-2 disabled:opacity-55 disabled:cursor-not-allowed`}
            aria-label="Passi singoli"
            aria-haspopup="menu"
            aria-expanded={splitOpen}
            disabled={disabled || isBusy}
            onClick={(e) => {
              e.stopPropagation();
              setSplitOpen((prev) => !prev);
            }}
            data-testid="split-caret"
          >
            <svg
              className="w-3 h-3 fill-none stroke-white stroke-[1.6] [stroke-linecap:round] [stroke-linejoin:round]"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path d="M6 9l6 6 6-6" />
            </svg>
          </button>

          {splitOpen && (
            <div
              role="menu"
              className="absolute top-[calc(100%+6px)] left-0 min-w-[300px] bg-card border border-line-2 rounded-lg shadow-popover py-1.5 z-[60] max-md:static max-md:mt-2 max-md:min-w-0 max-md:basis-full max-md:shadow-none"
              data-testid="split-menu"
            >
              <div className="text-[10.5px] uppercase tracking-[0.08em] text-ink-3 px-[10px] pb-1.5 pt-2 font-medium">
                Esegui un singolo passo
              </div>
              {SPLIT_ITEMS.map((item) => (
                <button
                  key={item.id}
                  role="menuitem"
                  type="button"
                  className="flex items-center gap-[10px] w-full px-[10px] py-2 border-none bg-transparent text-left rounded-sm hover:bg-paper-2 transition-colors duration-100 cursor-pointer max-md:py-3"
                  onClick={() => {
                    setSplitOpen(false);
                    onSyncStep(item.id);
                  }}
                >
                  <span
                    className={`w-7 h-7 rounded-[6px] inline-grid place-items-center shrink-0 ${item.bgClass} ${item.textClass}`}
                  >
                    {item.icon}
                  </span>
                  <span className="flex flex-col gap-px min-w-0">
                    <span className="text-[13px] font-medium text-ink">{item.label}</span>
                    <span className="text-[11.5px] text-ink-3">{item.desc}</span>
                  </span>
                </button>
              ))}
              {/* Tracking AS (Davide 2026-09-23: «mettilo sotto Sincronizza, tanto
                  e' automatico»): stesso one-click, da passo del menu. */}
              <button
                role="menuitem"
                type="button"
                data-testid="allservice-tracking-btn"
                disabled={trackingDisabled}
                title="Scarica i tracking da AllService e li invia automaticamente a Poleepo"
                className="flex items-center gap-[10px] w-full px-[10px] py-2 border-t border-line bg-transparent text-left rounded-sm hover:bg-paper-2 transition-colors duration-100 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                onClick={() => {
                  setSplitOpen(false);
                  void runTracking();
                }}
              >
                <span className="w-7 h-7 rounded-[6px] inline-grid place-items-center shrink-0 bg-amber-soft text-ink">
                  <IconTracking className="w-[14px] h-[14px] fill-none stroke-current stroke-[1.6] [stroke-linecap:round] [stroke-linejoin:round]" />
                </span>
                <span className="flex flex-col gap-px min-w-0">
                  <span className="text-[13px] font-medium text-ink">Tracking AllService</span>
                  <span className="text-[11.5px] text-ink-3">
                    Scarica i tracking AS e li carica su Poleepo
                  </span>
                </span>
              </button>
            </div>
          )}
        </div>

        {/* Ordina al fornitore — split-button UNICO (Davide 2026-08-13) al
            posto dei cinque tasti per-fornitore. Corpo: parte diretto se la
            selezione e' di un fornitore solo, apre la tendina se ce ne sono
            piu' d'uno. Caret: tendina sempre (per l'"Invio forzato" Cosma e
            per scegliere l'ordine con cui partire). Primo della fila: e' il
            passo che precede LDV e tracking. */}
        <div
          ref={supplierSplitRef}
          className="relative inline-flex items-stretch max-md:flex max-md:flex-wrap"
          data-testid="supplier-order-split"
        >
          <button
            type="button"
            disabled={supplierOrderDisabled}
            title={supplierOrderTooltip()}
            data-testid="supplier-order-btn"
            aria-haspopup={soloFlow ? undefined : "menu"}
            aria-expanded={soloFlow ? undefined : supplierMenuOpen}
            onClick={onSupplierOrderClick}
            className={[
              actionBtn,
              "rounded-l-full bg-accent-fill text-white border border-accent-fill",
              supplierOrderDisabled ? fillOff : fillHover,
            ].join(" ")}
          >
            {supplierOrderBusy ? (
              <MiniSpinner />
            ) : (
              <IconSend className="w-[14px] h-[14px] fill-none stroke-current stroke-[1.6] [stroke-linecap:round] [stroke-linejoin:round]" />
            )}
            {supplierOrderLabel}
          </button>
          <button
            type="button"
            disabled={supplierOrderDisabled}
            aria-label="Scegli il fornitore a cui ordinare"
            aria-haspopup="menu"
            aria-expanded={supplierMenuOpen}
            data-testid="supplier-order-caret"
            onClick={(e) => {
              e.stopPropagation();
              setSupplierMenuOpen((prev) => !prev);
            }}
            className={[
              actionCaret,
              "rounded-r-full bg-accent-fill text-white border border-accent-fill border-l-white/25",
              supplierOrderDisabled ? fillOff : fillHover,
            ].join(" ")}
          >
            <IconCaretDown className="w-3 h-3 fill-none stroke-current stroke-[1.6] [stroke-linecap:round] [stroke-linejoin:round]" />
          </button>

          {supplierMenuOpen && (
            <div
              role="menu"
              className="absolute top-[calc(100%+6px)] left-0 min-w-[300px] bg-card border border-line-2 rounded-lg shadow-popover py-1.5 z-[60] max-md:static max-md:mt-2 max-md:min-w-0 max-md:basis-full max-md:shadow-none"
              data-testid="supplier-order-menu"
            >
              <div className="text-[10.5px] uppercase tracking-[0.08em] text-ink-3 px-[10px] pb-1.5 pt-2 font-medium">
                Fornitori nella selezione
              </div>
              {supplierFlows.map((flow) => (
                <button
                  key={flow.id}
                  role="menuitem"
                  type="button"
                  data-testid={`supplier-order-item-${flow.id}`}
                  className="flex items-center gap-[10px] w-full px-[10px] py-2 border-none bg-transparent text-left rounded-sm hover:bg-paper-2 transition-colors duration-100 cursor-pointer max-md:py-3"
                  onClick={() => {
                    setSupplierMenuOpen(false);
                    flow.run();
                  }}
                >
                  <span className="w-7 h-7 rounded-[6px] inline-grid place-items-center shrink-0 bg-[rgba(180,120,40,0.14)] text-accent">
                    <IconSend className="w-[14px] h-[14px] fill-none stroke-current stroke-[1.6] [stroke-linecap:round] [stroke-linejoin:round]" />
                  </span>
                  <span className="flex flex-col gap-px min-w-0">
                    <span className="text-[13px] font-medium text-ink">
                      {flow.label} ({flow.count})
                    </span>
                    <span className="text-[11.5px] text-ink-3">{flow.desc}</span>
                  </span>
                </button>
              ))}
              {/* "Invio forzato" Cosma: era la voce del vecchio split-button
                  dedicato. Vive qui, e SOLO quando ci sono ordini Cosma
                  selezionati — altrove non avrebbe nulla da forzare. */}
              {cosmaSelected.length > 0 && (
                <button
                  role="menuitem"
                  type="button"
                  data-testid="cosma-force-item"
                  className="flex items-center gap-[10px] w-full px-[10px] py-2 border-none bg-transparent text-left rounded-sm hover:bg-paper-2 transition-colors duration-100 cursor-pointer border-t border-line"
                  onClick={() => {
                    setSupplierMenuOpen(false);
                    ordina.runCosma(cosmaSelected, true);
                  }}
                >
                  <span className="w-7 h-7 rounded-[6px] inline-grid place-items-center shrink-0 bg-[rgba(180,120,40,0.14)] text-accent">
                    <IconSync className="w-[14px] h-[14px] fill-none stroke-current stroke-[1.6] [stroke-linecap:round] [stroke-linejoin:round]" />
                  </span>
                  <span className="flex flex-col gap-px min-w-0">
                    <span className="text-[13px] font-medium text-ink">Cosma · invio forzato</span>
                    <span className="text-[11.5px] text-ink-3">
                      Ri-invia anche ordini già caricati
                    </span>
                  </span>
                </button>
              )}
            </div>
          )}
        </div>

        {/* Genera LDV — bottone unico che copre AS (xlsx) + Poleepo (PDF).
            Smista internamente in base alla selezione, lancia i due bulk
            in parallelo, restituisce un esito combinato. Risolve la
            confusione "2 bottoni con stessa label" pre-2026-05-28. */}
        <button
          type="button"
          disabled={unifiedLdvDisabled}
          title={unifiedLdvTooltip()}
          data-testid="ldv-btn"
          onClick={() => void runUnifiedLdv()}
          className={[
            actionBtn,
            "rounded-full bg-blue-fill text-white border border-blue-fill",
            unifiedLdvDisabled ? fillOff : fillHover,
          ].join(" ")}
        >
          {unifiedLdvBusy ? (
            <MiniSpinner />
          ) : (
            <IconCheckSquare className="w-[14px] h-[14px] fill-none stroke-current stroke-[1.6] [stroke-linecap:round] [stroke-linejoin:round]" />
          )}
          {unifiedLdvBusy
            ? "Genero…"
            : selectedOrders.length > 1
              ? `Genera LDV (${selectedOrders.length})`
              : "Genera LDV"}
        </button>

        {/* Stato ordine — l'UNICO controllo di STATO della toolbar (tutto il
            resto sono azioni operative). Reso come segmento coeso
            «Stato [tendina] [Applica]» invece della tendina nuda persa fra i
            tasti: prima i due elementi non si leggevano come una cosa sola, e
            la tendina sembrava un filtro. Resta la <select> NATIVA — copre i 9
            stati Poleepo senza portarsi dietro un picker. A destra dal 2026-09-23. */}
        {onBulkChangeStatus && (
          <div
            data-testid="bulk-status-group"
            title={bulkStatusTitle}
            className={[
              `ml-auto inline-flex items-stretch ${ACTION_H} rounded-full border transition-colors duration-[120ms] max-md:ml-0 max-md:flex max-md:rounded-xl`,
              bulkStatusDisabled ? "border-line opacity-60" : "border-line-2",
            ].join(" ")}
          >
            <label
              htmlFor="bulk-status-select"
              className="inline-flex items-center rounded-l-full border-r border-line bg-paper-2 pl-3.5 pr-2.5 text-[12.5px] font-semibold text-ink max-md:px-3.5 max-md:text-[13px] max-md:normal-case max-md:tracking-normal max-md:text-ink"
            >
              Stato
            </label>
            <select
              id="bulk-status-select"
              value={statusToSet}
              onChange={(e) => setStatusToSet(e.target.value)}
              disabled={bulkStatusDisabled}
              // Niente `py-*`: l'altezza la detta il gruppo (ACTION_H) e
              // `items-stretch` la propaga qui dentro.
              className="border-0 bg-card px-1.5 text-[12.5px] text-ink outline-none focus-visible:ring-2 focus-visible:ring-accent-soft disabled:cursor-not-allowed max-md:min-w-0 max-md:flex-1 max-md:text-[15px]"
              aria-label="Nuovo stato ordine"
            >
              {POLEEPO_BULK_STATUSES.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <button
              type="button"
              data-testid="bulk-status-apply"
              onClick={() => {
                if (!onBulkChangeStatus || selectedOrders.length === 0) return;
                setBulkStatusBusy(true);
                const refs = selectedOrders.map((o) => o.id);
                void onBulkChangeStatus(refs, statusToSet).finally(() => {
                  setBulkStatusBusy(false);
                });
              }}
              disabled={bulkStatusDisabled}
              className={[
                "inline-flex items-center gap-1.5 rounded-r-full border-l border-line px-3.5 text-[12.5px] font-semibold transition-all duration-[120ms] max-md:px-4 max-md:text-[15px] max-md:font-semibold",
                // Riposo `text-ink-2` (non `text-accent`): su bianco l'accent
                // sta a 3,9:1, sotto la soglia WCAG per il testo normale.
                // Hover `bg-accent-2` e non `bg-accent`: pieno + testo bianco
                // e' di nuovo 3,9:1, mentre accent-2 sta a 4,86:1 —
                // `shared/contrast.test.ts` lo verifica sui sorgenti veri.
                bulkStatusDisabled
                  ? "cursor-not-allowed bg-paper-2 text-ink-3"
                  : "bg-card text-ink-2 hover:bg-accent-2 hover:text-white",
              ].join(" ")}
            >
              {bulkStatusBusy && <MiniSpinner />}
              {bulkStatusBusy
                ? "Applico…"
                : selectedOrders.length > 1
                  ? `Applica (${selectedOrders.length})`
                  : "Applica"}
            </button>
          </div>
        )}
      </div>

      {ordina.dialogs}

      {/* Conferma cumulativa switch SDA→Poste Delivery per gli ordini Poleepo
          oltre 15 colli. Conferma → bulk con consenso; Annulla → bulk normale
          (gli over-soglia risultano saltati nel riepilogo). */}
      {carrierSwitchItems && (
        <CarrierSwitchConfirmDialog
          mode="bulk"
          items={carrierSwitchItems}
          onConfirm={confirmCarrierSwitch}
          onCancel={cancelCarrierSwitch}
        />
      )}
    </div>
  );
}
